import React, { useState } from "react";
import { Edit, Layers } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Typography } from "@mui/material";

import { Progress } from "ui";

import { useGetAccountModulesQuery } from "fond/api";
import { useCurrentProject } from "fond/hooks/useCurrentProject";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import OverlaysSettingModal from "fond/map/Overlays/OverlaysSettingModal";
import { dataPrepStageConfig } from "fond/project/panels/dataPrepStages";
import PanelGroup from "fond/project/panels/InputPanel/PanelGroup";
import { DataPrepGenerateOptions, toDataPrepRunParameters } from "fond/project/panels/RunDataPrep/dataPrepRunParameters";
import RunDataPrep from "fond/project/panels/RunDataPrep/RunDataPrep";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import { useMuPowerDataPrepStatus } from "./useMuPowerDataPrepStatus";

const stageConfig: Record<string, { label: string }> = {
  ...dataPrepStageConfig,
  // TODO: Remove these legacy `mu_data_prep` stages (FND-4902) once no executions created before the stage rename remain.
  base_data: { label: "Processing base data" },
  demand: { label: "Generating demand" },
  ug_gen: { label: "Generating underground network" },
  output: { label: "Preparing output" },
};

interface DataPrepPanelProps {
  isReadOnly?: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ isReadOnly }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const dataPrepStatus = useMuPowerDataPrepStatus(versionId);
  const canRun = dataPrepStatus.hasRequiredInputLayers;
  const project = useCurrentProject();
  const { open } = useStackedNavigationContext();
  const { workflowState, stages, runWorkflow, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.DATA_PREP,
  });
  const [serviceRoads, setServiceRoads] = useState<boolean>(false);
  const [generate, setGenerate] = useState<DataPrepGenerateOptions>({ underground: true, leadins: true });
  const [showOverlays, setShowOverlays] = useState(false);
  const { data: accountModules } = useGetAccountModulesQuery(project?.Account.ID ?? "");
  const hasOverlaysModule = accountModuleCheck(accountModules, Actions.CUSTOM_OVERLAYS);
  const canEditOverlays = usePermissionCheck(Actions.CUSTOM_OVERLAYS, project?.Permission.Level);

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const isComplete = dataPrepStatus.isDataPrepComplete && !isInProgress;

  const handleRunDataPrep = () => {
    runWorkflow?.({ ServiceRoads: serviceRoads, ...toDataPrepRunParameters(generate) });
  };

  const handleCancelDataPrep = () => {
    cancelWorkflow?.();
  };

  const handleModifyData = () => {
    open("modification-muPowerBaseData");
  };

  const handleOpenOverlays = () => {
    setShowOverlays(true);
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Upload required layers</AlertTitle>
          Please upload all input layers before running data preparation.
        </Alert>
      )}

      {canRun && !isComplete && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Data is ready for preparation.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          There was an error running data preparation. Please try again.
        </Alert>
      )}

      {isComplete && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isInProgress && (
        <PanelGroup title="Data preparation">
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" focused={false} sx={{ fontSize: (theme) => theme.typography.size.small, fontWeight: 400, mb: 0.5 }}>
              Streets
            </FormLabel>
            <FormGroup sx={{ pl: 1 }}>
              <FormControlLabel
                sx={{ alignItems: "flex-start" }}
                label={
                  <>
                    <Typography variant="body3" display="block">
                      Include service roads
                    </Typography>
                    <Typography fontSize={11} lineHeight="1.4" color={(theme) => theme.palette.biarri.secondary.grey}>
                      Parking aisles, driveways, alleys and similar routes
                    </Typography>
                  </>
                }
                control={
                  <Checkbox
                    size="small"
                    sx={{ py: 0.25 }}
                    name="serviceRoads"
                    checked={serviceRoads}
                    onChange={(event) => setServiceRoads(event.target.checked)}
                  />
                }
                disabled={isReadOnly || !canRun}
              />
            </FormGroup>
          </FormControl>
          <Box mt={1}>
            <RunDataPrep
              status={dataPrepStatus}
              canRun={Boolean(runWorkflow) && canRun}
              readOnly={Boolean(isReadOnly) || !canRun}
              generate={generate}
              onGenerateChange={setGenerate}
              onRun={handleRunDataPrep}
            />
          </Box>
        </PanelGroup>
      )}

      {isInProgress && (
        <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!cancelWorkflow || isReadOnly} onClick={handleCancelDataPrep}>
          Cancel
        </Button>
      )}

      {hasOverlaysModule && canEditOverlays && (
        <Box mt={1}>
          <Typography fontWeight="500" mb={0.5}>
            Reference data
          </Typography>
          <StackedNavigationButton
            icon={<Layers />}
            title="Manage overlays"
            onClick={handleOpenOverlays}
            disabled={isReadOnly}
            iconSize="small"
            variant="outlined"
            sx={{ m: 0 }}
          />
        </Box>
      )}

      {isComplete && (
        <Box mt={1}>
          <Typography fontWeight="500" mb={0.5}>
            Refine
          </Typography>
          <StackedNavigationButton icon={<Edit />} title="Modify data" onClick={handleModifyData} sx={{ m: 0 }} disabled={isReadOnly} />
        </Box>
      )}

      {canEditOverlays && <OverlaysSettingModal open={showOverlays} onClose={() => setShowOverlays(false)} />}
    </>
  );
};

export default DataPrepPanel;
